import React from 'react';
import { reportError } from '../utils/errorHandling';

function BackButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
            <i className="fas fa-arrow-left mr-2"></i>
            Back
        </button>
    );
}

function DonationHeader() {
    return (
        <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">Recycle Your PC</h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
                Free computer recycling with secure data destruction. Your recycled PC will be refurbished to support sustainable computing initiatives
                and provide technology access to underserved communities.
            </p>
        </div>
    );
}

function TabNavigation({ activeTab, onTabChange }) {
    return (
        <div className="flex space-x-4 mb-8">
            <button
                onClick={() => onTabChange('donate')}
                className={`px-4 py-2 rounded-lg ${
                    activeTab === 'donate'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
            >
                Recycle
            </button>
            <button
                onClick={() => onTabChange('track')}
                className={`px-4 py-2 rounded-lg ${
                    activeTab === 'track'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
            >
                Track Recycling
            </button>
            <button
                onClick={() => onTabChange('faq')}
                className={`px-4 py-2 rounded-lg ${
                    activeTab === 'faq'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
            >
                FAQ
            </button>
        </div>
    );
}

function HardwareSelector({ selectedHardware, onHardwareSelect, onQuantityChange }) {
    const hardwareTypes = [
        {
            icon: "fa-laptop",
            title: "Laptops",
            description: "Working or repairable laptops, any brand",
            co2Impact: 80
        },
        {
            icon: "fa-desktop",
            title: "Desktop Computers",
            description: "Complete systems or components",
            co2Impact: 120
        },
        {
            icon: "fa-server",
            title: "Servers",
            description: "Enterprise servers and rack equipment",
            co2Impact: 200
        },
        {
            icon: "fa-microchip",
            title: "Components",
            description: "RAM, CPUs, GPUs, and other parts",
            co2Impact: 40
        },
        {
            icon: "fa-tablet-alt",
            title: "Tablets",
            description: "iPads, Android tablets, and similar devices",
            co2Impact: 60
        },
        {
            icon: "fa-mobile-alt",
            title: "Smartphones",
            description: "iPhones, Android phones in working condition",
            co2Impact: 30
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hardwareTypes.map((hardware) => (
                <div
                    key={hardware.title}
                    className={`p-4 rounded-lg border ${
                        selectedHardware.some(h => h.type === hardware.title)
                            ? 'border-green-500 bg-green-900/20'
                            : 'border-gray-700 bg-gray-800/50'
                    }`}
                >
                    <div className="flex items-center mb-2">
                        <i className={`fas ${hardware.icon} text-2xl mr-3 ${
                            selectedHardware.some(h => h.type === hardware.title)
                                ? 'text-green-400'
                                : 'text-gray-400'
                        }`}></i>
                        <h3 className="font-medium">{hardware.title}</h3>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">{hardware.description}</p>
                    {selectedHardware.some(h => h.type === hardware.title) && (
                        <div className="flex items-center">
                            <label className="text-sm mr-2">Quantity:</label>
                            <input
                                type="number"
                                min="1"
                                value={selectedHardware.find(h => h.type === hardware.title)?.quantity || 1}
                                onChange={(e) => onQuantityChange(hardware.title, e.target.value)}
                                className="w-20 px-2 py-1 bg-gray-700 rounded border border-gray-600"
                            />
                        </div>
                    )}
                    <button
                        onClick={() => onHardwareSelect(hardware)}
                        className={`mt-3 w-full px-3 py-2 rounded ${
                            selectedHardware.some(h => h.type === hardware.title)
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-green-600 hover:bg-green-700'
                        } text-white transition-colors`}
                    >
                        {selectedHardware.some(h => h.type === hardware.title)
                            ? 'Remove'
                            : 'Add'}
                    </button>
                </div>
            ))}
        </div>
    );
}

function ImpactDisplay({ totalImpact }) {
    return (
        <div className="bg-green-900/20 p-4 rounded-lg mt-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-medium text-white">Environmental Impact</h3>
                    <p className="text-sm text-gray-400">Estimated CO₂ saved</p>
                </div>
                <div className="text-2xl font-bold text-green-400">
                    {totalImpact} kg
                </div>
            </div>
        </div>
    );
}

function SubmissionSuccess({ onReset }) {
    return (
        <div className="text-center py-8">
            <div className="mb-6">
                <i className="fas fa-check-circle text-6xl text-green-400"></i>
            </div>
            <h2 className="text-2xl font-bold mb-4">Thank You for Recycling Your PC!</h2>
            <p className="text-gray-400 mb-8">
                We've received your PC recycling request and will contact you shortly to arrange pickup.
            </p>
            <button
                onClick={onReset}
                className="voxel-button"
            >
                Recycle Another PC
            </button>
        </div>
    );
}

function ErrorMessage({ message }) {
    return (
        <div className="mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
            <div className="flex items-center text-red-400">
                <i className="fas fa-exclamation-circle text-xl mr-3"></i>
                <span>{message || 'There was an error submitting your donation. Please try again.'}</span>
            </div>
        </div>
    );
}

function DonationsPage({ user, onBackClick }) {
    try {
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        const [submitStatus, setSubmitStatus] = React.useState(null);
        const [errorMessage, setErrorMessage] = React.useState('');
        const [activeTab, setActiveTab] = React.useState('donate');
        const [selectedHardware, setSelectedHardware] = React.useState([]);
        const [isServiceReady, setIsServiceReady] = React.useState(false);
        const [formData, setFormData] = React.useState({
            name: user ? user.name : '',
            email: user ? user.email : '',
            phone: '',
            organization: '',
            address: '',
            city: '',
            postalCode: '',
            country: 'United Kingdom',
            pickupDate: '',
            notes: ''
        });

        React.useEffect(() => {
            if (window.serviceStatus && window.serviceStatus.initialized) {
                setIsServiceReady(true);
            }

            const handleServicesReady = () => {
                console.log("Services are ready - DonationsPage received event");
                setIsServiceReady(true);
            };

            document.addEventListener('services-ready', handleServicesReady);

            if (window.initializeServices && !window.serviceStatus?.initialized) {
                window.initializeServices().then(success => {
                    if (success) {
                        setIsServiceReady(true);
                    }
                });
            }

            return () => {
                document.removeEventListener('services-ready', handleServicesReady);
            };
        }, []);

        const handleHardwareSelect = (hardware) => {
            const existingIndex = selectedHardware.findIndex(h => h.type === hardware.title);
            if (existingIndex >= 0) {
                setSelectedHardware(selectedHardware.filter(h => h.type !== hardware.title));
            } else {
                setSelectedHardware([...selectedHardware, { type: hardware.title, quantity: 1, co2Impact: hardware.co2Impact }]);
            }
        };

        const handleQuantityChange = (type, quantity) => {
            setSelectedHardware(selectedHardware.map(h =>
                h.type === type ? { ...h, quantity: parseInt(quantity) || 0 } : h
            ));
        };

        const handleInputChange = (e) => {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value
            });
        };

        const handleDonationSubmit = async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            setSubmitStatus(null);
            setErrorMessage('');

            try {
                if (!selectedHardware.length) {
                    throw new Error('Please select at least one hardware item to donate');
                }

                if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.postalCode) {
                    throw new Error('Please fill in all required fields');
                }

                if (!isServiceReady || !window.donationService || !window.donationService.createDonation) {
                    if (window.initializeServices) {
                        console.log("Attempting to initialize services before submission...");
                        const success = await window.initializeServices();
                        if (!success || !window.donationService?.createDonation) {
                            throw new Error('Donation service is not available. Please try again later.');
                        }
                    } else {
                        throw new Error('Donation service is not available. Please try again later.');
                    }
                }

                const donationData = {
                    ...formData,
                    hardware: selectedHardware,
                    user_id: user?.id,
                    status: 'pending',
                    created_at: new Date(),
                    co2_saved: selectedHardware.reduce((sum, item) => sum + (item.co2Impact * item.quantity), 0)
                };

                console.log("Submitting donation with data:", donationData);

                const donationId = await window.donationService.createDonation(donationData);

                if (donationId) {
                    setSubmitStatus('success');
                    if (window.logAnalytics) {
                        window.logAnalytics('donation_submitted', {
                            donation_id: donationId,
                            user_id: user?.id,
                            hardware_count: selectedHardware.length,
                            co2_saved: donationData.co2_saved
                        });
                    }
                } else {
                    throw new Error('Failed to submit PC recycling request. Please try again.');
                }
            } catch (error) {
                console.error('Donation submission error:', error);
                setSubmitStatus('error');
                setErrorMessage(error.message || 'There was an error submitting your donation. Please try again.');
                if (window.reportError) {
                    window.reportError(error);
                }
            } finally {
                setIsSubmitting(false);
            }
        };

        const resetForm = () => {
            setFormData({
                name: user ? user.name : '',
                email: user ? user.email : '',
                phone: '',
                organization: '',
                address: '',
                city: '',
                postalCode: '',
                country: 'United Kingdom',
                pickupDate: '',
                notes: ''
            });
            setSelectedHardware([]);
            setSubmitStatus(null);
            setErrorMessage('');
        };

        const calculateTotalImpact = () => {
            return selectedHardware.reduce((sum, item) => sum + (item.co2Impact * item.quantity), 0);
        };

        const ServiceNotReadyMessage = () => (
            <div className="bg-yellow-900/20 p-4 rounded-lg mb-6">
                <div className="flex items-center">
                    <i className="fas fa-exclamation-triangle text-yellow-400 mr-3"></i>
                    <p className="text-yellow-400">
                        The donation service is initializing. Please wait a moment...
                    </p>
                </div>
            </div>
        );

        return (
            <section className="min-h-screen bg-gray-900 text-white py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <BackButton onClick={onBackClick} />
                        <DonationHeader />
                        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

                        {activeTab === 'donate' && (
                            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">                                    <div>
                                        <h2 className="text-2xl font-bold mb-6">Select PC Hardware to Recycle</h2>
                                        <HardwareSelector
                                            selectedHardware={selectedHardware}
                                            onHardwareSelect={handleHardwareSelect}
                                            onQuantityChange={handleQuantityChange}
                                        />

                                        {selectedHardware.length > 0 && (
                                            <ImpactDisplay totalImpact={calculateTotalImpact()} />
                                        )}
                                    </div>

                                    <div>
                                        {!isServiceReady && <ServiceNotReadyMessage />}

                                        {submitStatus === 'success' ? (
                                            <SubmissionSuccess onReset={resetForm} />
                                        ) : (
                                            <form onSubmit={handleDonationSubmit} className="space-y-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Full Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={user}
                                                            className="form-input w-full"
                                                            data-name="name-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Email <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={user}
                                                            className="form-input w-full"
                                                            data-name="email-input"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Phone <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="form-input w-full"
                                                            data-name="phone-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Organization
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="organization"
                                                            value={formData.organization}
                                                            onChange={handleInputChange}
                                                            className="form-input w-full"
                                                            data-name="organization-input"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Address <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="address"
                                                        value={formData.address}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="form-input w-full"
                                                        data-name="address-input"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            City <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="city"
                                                            value={formData.city}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="form-input w-full"
                                                            data-name="city-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Postal Code <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="postalCode"
                                                            value={formData.postalCode}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="form-input w-full"
                                                            data-name="postal-code-input"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium mb-2">
                                                            Country <span className="text-red-500">*</span>
                                                        </label>
                                                        <select
                                                            name="country"
                                                            value={formData.country}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="form-select w-full"
                                                            data-name="country-select"
                                                        >
                                                            <option value="United Kingdom">United Kingdom</option>
                                                            <option value="United States">United States</option>
                                                            <option value="Canada">Canada</option>
                                                            <option value="Australia">Australia</option>
                                                            <option value="Germany">Germany</option>
                                                            <option value="France">France</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Preferred Pickup Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        name="pickupDate"
                                                        value={formData.pickupDate}
                                                        onChange={handleInputChange}
                                                        min={new Date().toISOString().split('T')[0]}
                                                        className="form-input w-full"
                                                        data-name="pickup-date-input"
                                                    />
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        We'll contact you to confirm the exact pickup time
                                                    </p>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Additional Notes
                                                    </label>
                                                    <textarea
                                                        name="notes"
                                                        value={formData.notes}
                                                        onChange={handleInputChange}
                                                        rows="4"
                                                        className="form-textarea w-full"
                                                        placeholder="Any specific details about the hardware or pickup arrangements"
                                                        data-name="notes-textarea"
                                                    ></textarea>
                                                </div>

                                                <div className="pt-4 border-t border-gray-700">                                                    <div className="bg-green-900/20 p-4 rounded-lg mb-4">
                                                        <div className="flex items-center mb-2">
                                                            <i className="fas fa-info-circle text-green-400 mr-2"></i>
                                                            <h4 className="font-medium text-white">Important Notice</h4>
                                                        </div>
                                                        <p className="text-sm text-gray-300">
                                                            By submitting this form, you're recycling hardware through <strong>SCN Recycling</strong> (our for-profit company) which supports both our sustainable business operations and enables SCN charity's mission to provide technology solutions to underserved communities.
                                                        </p>
                                                    </div>

                                                    <div className="bg-blue-900/20 p-4 rounded-lg mb-4">
                                                        <div className="flex items-center mb-2">
                                                            <i className="fas fa-server text-blue-400 mr-2"></i>
                                                            <h4 className="font-medium text-white">Sustainable Compute Network</h4>
                                                        </div>                                                        <p className="text-sm text-gray-300 mb-2">
                                                            Many of your recycled devices will be repurposed as nodes in our distributed cloud computing network, powered entirely by green energy. This innovative reuse extends their useful life while reducing demand for new hardware production.
                                                        </p>
                                                        <div className="flex items-start mt-3">
                                                            <div className="flex-shrink-0 mt-1">
                                                                <i className="fas fa-recycle text-green-400"></i>
                                                            </div>
                                                            <p className="text-sm text-gray-300 ml-2">
                                                                <strong className="text-white">Our commitment:</strong> All end-of-life products are ethically recycled through certified partners and <strong className="text-green-400">NEVER</strong> sent to landfill.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="submit"
                                                        id="donation-submit-button"
                                                        disabled={isSubmitting || selectedHardware.length === 0}
                                                        className={`voxel-button w-full ${
                                                            selectedHardware.length === 0
                                                                ? 'bg-gray-600 opacity-50 cursor-not-allowed hover:transform-none'
                                                                : ''
                                                        }`}
                                                        data-name="submit-button"
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="flex items-center justify-center">
                                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                                Submitting...
                                                            </span>
                                                        ) : (                                                            <span className="flex items-center justify-center">
                                                                <i className="fas fa-heart mr-2"></i>
                                                                Recycle My PC
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                        )}

                                        {submitStatus === 'error' && (
                                            <ErrorMessage message={errorMessage} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'track' && (
                            <DonationTracker user={user} />
                        )}

                        {activeTab === 'faq' && (
                            <>
                                <DonationFAQ />
                                <LocalSearchOptimization />
                                <SocialProofSection />
                            </>
                        )}
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('DonationsPage error:', error);
        if (window.reportError) {
            window.reportError(error);
        }
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="voxel-card max-w-md text-center">
                    <i className="fas fa-exclamation-triangle text-4xl mb-4 text-red-400"></i>
                    <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
                    <p className="mb-6">We encountered an error loading the donations page. Please try again later.</p>
                    <button onClick={onBackClick} className="voxel-button">
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }
}

// Export the DonationsPage component
export default DonationsPage;